// /impersonate?grant=<token>[&return_to=/quotes/<id>] — Path A landing page.
//
// Nexus redirects sales here after issuing the grant. We consume the
// token, transition the SPA into an impersonation session, and route
// to return_to if provided (must be a safe relative portal path),
// otherwise fall back to the customer's quote list. Errors land on a
// small recovery card (the grant TTL is short — by the time the user
// reads this, the grant has likely already expired and they should
// retry from Nexus).

import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { extractApiError } from '../api/client.js';
import { useAuth } from '../auth/AuthContext.js';

// The grant exchange is a one-shot fired on mount. Because it's an /auth/
// endpoint the axios interceptor deliberately won't retry it, so a single
// transport blip (dev proxy warming up its upstream connection on the first
// proxied call after a backend restart; an ACA revision rollover in prod)
// dead-ends the whole flow until the rep manually re-triggers from Nexus.
//
// Retry ONLY on a transport-level failure — either no HTTP response at all
// (network reset / timeout) or a bare gateway status from the proxy with no
// error envelope. Both mean the request never reached the exchange handler,
// so the one-time grant was NOT consumed and re-sending it is safe. A real
// rejection (expired / already used / invalid) always carries an envelope and
// must surface immediately — retrying it would be pointless and could mask the
// reason. This is the "if I redo, it works" behaviour, done automatically.
// The exchange auto-fires the instant Nexus redirects here, so it is uniquely
// exposed to a backend that is briefly unavailable — a dev `node --watch`
// restart (1–several seconds), or an ACA revision rollover in prod. Retry
// across a window long enough to outlast that, so a transient outage doesn't
// dead-end the rep on a one-time grant (which, per the transaction rollback,
// is not consumed by a failed attempt).
//
// A `tsx` dev restart + Prisma reconnect can take several seconds, so the window
// spans 4 attempts with escalating backoff (~0.8s + 1.6s + 2.4s ≈ 4.8s of waiting
// across the 3 gaps) — comfortably longer than a `node --watch` restart, which a
// 3-attempt/~1.8s window could land entirely inside.
const MAX_EXCHANGE_ATTEMPTS = 4;
const RETRY_BACKOFF_MS = (attempt: number): number => Math.min(800 * attempt, 2400);

function isTransientTransportError(err: unknown): boolean {
  const response = (err as { response?: { status?: number } } | undefined)?.response;
  // No response → network reset / proxy-not-ready / timeout.
  if (!response) return true;
  // Any 5xx that carries NO error envelope is a transport artifact, not a
  // handler decision: the dev backend resetting the connection mid-recompile
  // (tsx watch), or a proxy/gateway surfacing an upstream reset as an
  // empty-body 500/502/503/504 (an ACA revision rollover does the same in
  // prod). The exchange runs in a transaction that rolls back on such a
  // failure, so the one-time grant is NOT consumed and re-sending is safe. A
  // genuine backend error ALWAYS carries an envelope (extractApiError != null)
  // and is not retried, so its real message still surfaces immediately.
  const status = response.status ?? 0;
  return status >= 500 && status <= 599 && extractApiError(err) === null;
}

export function ImpersonateExchange(): JSX.Element {
  const auth = useAuth();
  const navigate = useNavigate();
  const [search] = useSearchParams();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  // React 18 StrictMode mounts effects twice in dev; the grant is one-
  // time-use so the second attempt will always fail. Guard against it.
  const consumed = useRef(false);

  useEffect(() => {
    if (consumed.current) return;
    const grant_token = search.get('grant');
    if (!grant_token) {
      setErrorMessage('Missing grant token. Please retry from Nexus.');
      return;
    }
    consumed.current = true;
    // Validate return_to: must be a relative path starting with /
    // (no protocol, no host) to prevent open-redirect abuse.
    const raw_return_to = search.get('return_to') ?? '';
    const return_to =
      raw_return_to.startsWith('/') && !raw_return_to.startsWith('//')
        ? raw_return_to
        : '/quotes';
    void (async () => {
      for (let attempt = 1; ; attempt++) {
        try {
          await auth.startImpersonationFromGrant(grant_token);
          navigate(return_to, { replace: true });
          return;
        } catch (err) {
          // Keep a console breadcrumb for DevTools — distinguishes a
          // routing/proxy miss (no response / HTML body) from a genuine
          // enveloped backend rejection, without surfacing internals on the card.
          const e = err as {
            response?: { status?: number };
            code?: string;
            message?: string;
          };
          console.error('[impersonate] exchange failed', {
            attempt,
            status: e.response?.status,
            code: e.code,
            message: e.message,
            transient: isTransientTransportError(err),
          });
          if (attempt < MAX_EXCHANGE_ATTEMPTS && isTransientTransportError(err)) {
            // Escalating backoff so the retry window outlasts a backend
            // restart rather than exhausting inside it.
            await new Promise((r) => setTimeout(r, RETRY_BACKOFF_MS(attempt)));
            continue;
          }
          setErrorMessage(
            extractApiError(err)?.message ?? 'Could not start the impersonation session.'
          );
          return;
        }
      }
    })();
  }, [search, auth, navigate]);

  if (errorMessage) {
    return (
      <div className="card">
        <h2>Could not start impersonation</h2>
        <p className="banner banner--error">{errorMessage}</p>
        <p>Open the customer record in Nexus and click the impersonation button again.</p>
      </div>
    );
  }

  return (
    <div className="card">
      <h2>Starting impersonation…</h2>
      <p>Hang tight, connecting to the customer's account.</p>
    </div>
  );
}
